// Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com
// See license file.
import { ref, computed, type Ref, watch, type ComputedRef } from "vue";
import Fuse from "fuse.js";
import _ from "lodash";
import type { Actor, Dentist, HealthCenter, Reference } from "@/types";

// Builds a search engine from Fuse given the config and the collection to parse.
// TODO: provide the FUSE search engine to search engins.
export function makeSearchEngine<T>(
  config_option: Fuse.IFuseOptions<T>,
  input: Ref<string>,
  source: ComputedRef<Array<T>>
) {
  const searchTerms = ref(input.value);
  const searchResults = ref([] as T[]) as Ref<T[]>;
  const searchEngine = computed(() => {
    if (source.value.length > 0) {
      console.log(
        `Search engine index update started for ${source.value.length} items`
      );
      return new Fuse(source.value, config_option);
    } else return null;
  });

  // Debounce changes to input into searchTerms that triggers a watch chain.
  // Since this is async and needs to be run once at startup, this is done with watch and no computed.
  watch(
    input,
    _.debounce(() => {
      searchTerms.value = input.value;
    }, 250)
  );

  watch(
    searchTerms,
    () => {
      if (searchTerms.value.length > 1) {
        if (!searchEngine.value) {
          console.log("Search engine not ready");
          return;
        }
        const limit = searchTerms.value.indexOf(":") > 3 ? 1 : 12; // Search by ID or by string
        const found = searchEngine.value.search(searchTerms.value, { limit });
        searchResults.value = found.map((a) => a.item);
      } else {
        searchResults.value = [];
      }
    },
    { immediate: true }
  );

  return { searchResult: searchResults };
}

// Finds the given references in the datasources efficently, returns the found entities and the not found entitites in two separate lists.
export function findStakeholder(
  toFind: Array<Reference>,
  actors: ComputedRef<{ list: Actor[]; index: Map<string, number> }>,
  dentists: ComputedRef<{ list: Dentist[]; index: Map<string, number> }>,
  healthCenters: ComputedRef<{
    list: HealthCenter[];
    index: Map<string, number>;
  }>
) {
  if (dentists && actors && healthCenters) {
    const notFound: Array<Reference> = [];
    const found = toFind
      .map((ref) => {
        if (!ref.id) {
          notFound.push(ref);
          return null;
        } else if (ref.type === "pract" || ref.id.startsWith("pract:")) {
          const key = dentists.value.index.get(ref.id);
          if (key !== undefined) {
            return dentists.value.list[key];
          } else {
            console.warn("Practician with id ", ref, "not found in index");
            notFound.push(ref);
          }
        } else if (ref.type === "cntr" || ref.id.startsWith("cntr:")) {
          const key = healthCenters.value.index.get(ref.id);
          if (key !== undefined) {
            return healthCenters.value.list[key];
          } else {
            console.warn("Health center with id ", ref, "not found in index");
            notFound.push(ref);
          }
        } else {
          const key = actors.value.index.get(ref.id);
          if (key !== undefined) {
            return actors.value.list[key];
          } else {
            console.warn("Actor with id ", ref, "not found in index");
            notFound.push(ref);
          }
        }
      })
      .filter((a) => a !== undefined && a !== null) as Array<
      Dentist | Actor | HealthCenter
    >;
    return { found, notFound };
  }
}

// Returns records for which names, phone numbers or emails are similar to the given element.
export function findDuplicate(collection: Actor[], element: Actor): Actor[] {
  return collection.filter((actor) => {
    const isPhoneMatch =
      (actor.contact?.phone_cellular &&
        element.contact?.phone_cellular &&
        actor.contact.phone_cellular.replace(/\s/g, "") ===
          element.contact.phone_cellular.replace(/\s/g, "")) ||
      (actor.contact?.phone_cellular &&
        element.contact?.phone_landline &&
        actor.contact.phone_cellular.replace(/\s/g, "") ===
          element.contact.phone_landline.replace(/\s/g, "")) ||
      (actor.contact?.phone_landline &&
        element.contact?.phone_cellular &&
        actor.contact.phone_landline.replace(/\s/g, "") ===
          element.contact.phone_cellular.replace(/\s/g, "")) ||
      (actor.contact?.phone_landline &&
        element.contact?.phone_landline &&
        actor.contact.phone_landline.replace(/\s/g, "") ===
          element.contact.phone_landline.replace(/\s/g, ""));
    const isEmailMatch =
      actor.contact?.email &&
      element.contact?.email &&
      actor.contact.email.replace(/\s/g, "").toLowerCase() ===
        element.contact.email.replace(/\s/g, "").toLowerCase();
    const isNameMatch =
      actor.last_name &&
      element.last_name &&
      actor.first_name &&
      element.first_name &&
      actor.last_name.replace(/\s|-/g, "").toLowerCase() ===
        element.last_name.replace(/\s|-/g, "").toLowerCase() &&
      actor.first_name.replace(/\s|-/g, "").toLowerCase() ===
        element.first_name.replace(/\s|-/g, "").toLowerCase();
    const isInstitutionMatch =
      actor.institution_name &&
      element.institution_name &&
      actor.institution_name.replace(/\s|-/g, "").toLowerCase() ===
        element.institution_name.replace(/\s|-/g, "").toLowerCase();
    return isPhoneMatch || isEmailMatch || isNameMatch || isInstitutionMatch;
  });
}
